import React, { Component } from 'react';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import Griddle, {
  selectors,
  utils,
  RowDefinition,
  ColumnDefinition,
} from 'griddle-react';

import { paginationHelperProptypes } from 'offRamp/propTypes';
import {
  offRamp as offRampPropType,
  offRampsSortProp,
  offRampsTablePropTypes,
} from '../propTypes';
import MobileColumn1 from '../components/OffRampsTable/MobileColumn1';
import MobileColumn2 from '../components/OffRampsTable/MobileColumn2';
import ColumnHeader from '../components/OffRampsTable/ColumnHeader';
import Layout from '../components/OffRampsTable/Layout';
import NextButton from '../components/OffRampsTable/NextButton';
import NoOffRampsYet from '../components/NoOffRampsYet';
import PageNumbers from '../components/OffRampsTable/PageNumbers';
import Pagination from '../components/OffRampsTable/Pagination';
import PaginationHelper from '../components/OffRampsTable/PaginationHelper';
import PreviousButton from '../components/OffRampsTable/PreviousButton';
import Reference from '../components/OffRampsTable/Reference';
import Timestamp from '../components/OffRampsTable/Timestamp';
import TradedAmount from '../components/OffRampsTable/TradedAmount';
import ConvertedAmount from '../components/OffRampsTable/ConvertedAmount';
import Status from '../components/OffRampsTable/Status';

import styles from '../components/OffRampsTable/index.module.css';

import { actionsMap } from '../tableUtils';
import { getOffRampsTable } from '../reducers';

const styleConfig = {
  classNames: {
    Table: styles.table,
    Row: styles.row,
    Cell: styles.cell,
    TableHeading: styles.tableHeading,
    TableHeadingCell: styles.tableHeadingCell,
  },
};

const handleRowClick = (e, props, onOffRampIdClick) => {
  e.preventDefault();
  const { id } = props.rowData;

  return onOffRampIdClick(id);
};

const EnhanceWithRowData = utils.connect((state, props) => ({
  rowData: selectors.rowDataSelector(state, props),
}));

const EnhanceWithOnRowClick = (onOffRampIdClick) => (OriginalComponent) =>
  EnhanceWithRowData((props) => (
    <OriginalComponent
      {...props}
      onClick={(e) => handleRowClick(e, props, onOffRampIdClick)}
    />
  ));

const OffRampsTableDesktop = ({
  components,
  data,
  page,
  pageSize,
  paginationEvents,
  sort,
  totalElements,
  paginationHelperData,
}) => (
  <div className={styles.tableDesktop}>
    <Griddle
      useExternal
      data={data}
      pageProperties={{
        currentPage: page,
        pageSize,
        recordCount: totalElements || data.length,
      }}
      components={components}
      sortProperties={[sort]}
      styleConfig={styleConfig}
      events={paginationEvents()}
    >
      <RowDefinition>
        <ColumnDefinition
          id="createdAt"
          title="Date"
          customComponent={Timestamp}
          customHeadingComponent={ColumnHeader}
        />
        <ColumnDefinition
          id="reference"
          title="Reference"
          customComponent={Reference}
          customHeadingComponent={ColumnHeader}
        />
        <ColumnDefinition
          id="tradedAmount"
          title="Original amount"
          customHeadingComponent={ColumnHeader}
          sortable={false}
          customComponent={TradedAmount}
        />
        <ColumnDefinition
          id="convertedAmount"
          title="Converted amount"
          customHeadingComponent={ColumnHeader}
          sortable={false}
          customComponent={ConvertedAmount}
        />
        <ColumnDefinition
          id="status"
          title="Status"
          customComponent={Status}
          customHeadingComponent={ColumnHeader}
          sortable={false}
        />
      </RowDefinition>
    </Griddle>
    {data.length > 0 && <PaginationHelper data={paginationHelperData} />}
  </div>
);

OffRampsTableDesktop.propTypes = {
  ...offRampsTablePropTypes,
};

const OffRampsTableMobile = ({
  components,
  data,
  page,
  pageSize,
  paginationEvents,
  paginationHelperData,
  sort,
  totalElements,
}) => {
  const processedData = data.map((order) => ({
    ...order,
    column1: {
      status: order.status,
      reference: order.reference,
      tradedAmount: order.tradedAmount,
      convertedAmount: order.convertedAmount,
    },
    column2: {
      status: order.status,
      createdAt: order.createdAt,
    },
  }));

  return (
    <div className={styles.tableMobile}>
      <Griddle
        useExternal
        data={processedData}
        pageProperties={{
          currentPage: page,
          pageSize,
          recordCount: totalElements || data.length,
        }}
        components={components}
        sortProperties={[sort]}
        styleConfig={styleConfig}
        events={paginationEvents()}
      >
        <RowDefinition>
          <ColumnDefinition id="column1" customComponent={MobileColumn1} />
          <ColumnDefinition id="column2" customComponent={MobileColumn2} />
        </RowDefinition>
      </Griddle>
      {data.length > 0 && <PaginationHelper data={paginationHelperData} />}
    </div>
  );
};

OffRampsTableMobile.propTypes = offRampsTablePropTypes;

class OffRampsTable extends Component {
  get components() {
    const { onOffRampIdClick } = this.props;

    return {
      Layout,
      NextButton,
      PreviousButton,
      PageDropdown: PageNumbers,
      SettingsToggle: () => null,
      Pagination,
      RowEnhancer: EnhanceWithOnRowClick(onOffRampIdClick),
    };
  }

  paginationEvents = () => {
    const { setPage, setSort, nextPage, previousPage } = this.props;
    return {
      onNext: () => nextPage(),
      onPrevious: () => previousPage(),
      onGetPage: (pageNumber) => setPage(pageNumber),
      onSort: (sortProperties) => setSort(sortProperties),
    };
  };

  render() {
    const {
      data,
      isEmpty,
      noDataYet,
      page,
      pageSize,
      paginationHelperData,
      sort,
      tableRef,
      totalElements,
      windowWidth,
    } = this.props;

    const classes = classNames(styles.griddleTableWrapper, {
      [styles.empty]: isEmpty,
    });

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={classes}
        ref={tableRef}
        data-test="payments-table"
      >
        {noDataYet ? (
          <NoOffRampsYet />
        ) : (
          <>
            {windowWidth > 1046 ? (
              <OffRampsTableDesktop
                components={this.components}
                data={data}
                isEmpty={isEmpty}
                page={page}
                pageSize={pageSize}
                paginationEvents={this.paginationEvents}
                paginationHelperData={paginationHelperData}
                sort={sort}
                totalElements={totalElements}
              />
            ) : (
              <OffRampsTableMobile
                components={this.components}
                data={data}
                page={page}
                pageSize={pageSize}
                paginationEvents={this.paginationEvents}
                paginationHelperData={paginationHelperData}
                sort={sort}
                totalElements={totalElements}
              />
            )}
          </>
        )}
      </motion.div>
    );
  }
}

OffRampsTable.propTypes = {
  data: PropTypes.arrayOf(offRampPropType).isRequired,
  isEmpty: PropTypes.bool.isRequired,
  nextPage: PropTypes.func.isRequired,
  noDataYet: PropTypes.bool,
  onOffRampIdClick: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  previousPage: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  sort: offRampsSortProp.isRequired,
  tableRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  totalElements: PropTypes.number.isRequired,
  windowWidth: PropTypes.number.isRequired,
  paginationHelperData: paginationHelperProptypes,
};

const mapStateToProps = (state) => {
  const { data, page, pageSize, totalElements, sort } = getOffRampsTable(state);

  return {
    data,
    page,
    pageSize,
    totalElements,
    sort,
  };
};

const mapDispatchToProps = (dispatch) => {
  const offRampActions = actionsMap;

  return {
    nextPage: () => dispatch(offRampActions.nextPage()),
    onOffRampIdClick: (offRampId) =>
      dispatch(push(offRampActions.onOffRampIdClick(offRampId))),
    previousPage: () => dispatch(offRampActions.previousPage()),
    setPage: (pageNumber) => dispatch(offRampActions.setPage(pageNumber)),
    setSort: (sortProperties) =>
      dispatch(offRampActions.setSort(sortProperties)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(OffRampsTable);
